const ALLOWED_QUERY_FIELDS = ['indexUid', 'q', 'limit', 'offset', 'filter', 'facets', 'sort'] as const

type AllowedQueryField = typeof ALLOWED_QUERY_FIELDS[number]

type RawMultiSearchQuery = Record<string, unknown>

type RawMultiSearchBody = {
    queries: RawMultiSearchQuery[]
} & Record<string, unknown>

/**
 * Projects an outgoing `multi-search` request body built by
 * `@meilisearch/instant-meilisearch` down to the fields the A4G Search API actually
 * understands: `indexUid`, `q`, `limit`/`offset` pagination (the adapter's
 * default — non-finite — pagination mode already speaks this; no `page`/`hitsPerPage`
 * translation is needed, see ticket #87's pagination design decision), `filter`,
 * `facets`, `sort`.
 *
 * Drops fields the adapter adds by default that the API ignores or doesn't support
 * (`attributesToRetrieve`, `attributesToHighlight`, `highlightPreTag`/
 * `highlightPostTag`, …) so the narrowed backend only ever receives a request shape it
 * actually implements.
 *
 * Bodies without a `queries` array (e.g. `facet-search` request) pass through
 * unchanged — this seam only projects multi-search queries.
 */
export function adaptSearchRequestBody(rawBody: unknown): unknown {
    if (!isMultiSearchBody(rawBody)) {
        return rawBody
    }

    return {
        ...rawBody,
        queries: rawBody.queries.map(pickAllowedQueryFields),
    }
}

function isMultiSearchBody(value: unknown): value is RawMultiSearchBody {
    return value != null
        && typeof value === 'object'
        && Array.isArray((value as { queries?: unknown }).queries)
}

function pickAllowedQueryFields(query: RawMultiSearchQuery): Partial<Record<AllowedQueryField, unknown>> {
    const picked: Partial<Record<AllowedQueryField, unknown>> = {}

    for (const field of ALLOWED_QUERY_FIELDS) {
        if (query[field] !== undefined) {
            picked[field] = query[field]
        }
    }

    // algoliasearch-helper issues an auxiliary query per refined disjunctive facet
    // (see its _getQueries()) to compute that facet's own counts/stats ignoring its own
    // filter — the mechanism that lets a refinement list stay multi-selectable and a
    // range input keep its full bounds after refining. That query always asks for
    // limit 0; the API requires limit >= 1. Its hits are never read (only
    // facetDistribution/facetStats, which reflect the full filtered set regardless of
    // limit), so bumping it to 1 is safe.
    if (picked.limit === 0) {
        picked.limit = 1
    }

    return picked
}
